import { colors } from "widgets/utils";

import { COORDINATE_TICK_HEIGHT, GROUP_HEADER_HEIGHT } from "../../settings.ts";
import { alignToLeftPixelGrid } from "../../utils/graphics/align-to-pixel-grid.ts";

/**
 * Draw the coordinates values on top of each group, in the group header.
 */
export function drawCoordinates(
  baseShift: number,
  contigLength: number,
  maxWidth: number,
  baseData: {
    basePerPixel: number;
    coordinateInterval: number;
    ctx: CanvasRenderingContext2D;
    endBase: number;
    firstCoord: number;
    scrollX: number;
  },
) {
  const { basePerPixel, coordinateInterval, ctx, endBase, firstCoord, scrollX } = baseData;

  // Not `RobotoMono` in the main thread as we use the application web font
  ctx.font = "9px Roboto Flex Mono";
  ctx.fillStyle = colors.textDefault;
  ctx.strokeStyle = colors.textDefault;
  ctx.lineWidth = 1;
  ctx.textAlign = "center";

  drawHorizontalLine(ctx, 0);

  // Draw ticks and labels
  ctx.save();

  const firstTickTextWidth = ctx.measureText("0").width;
  const lastTickTextWidth = ctx.measureText(contigLength.toLocaleString("en-US")).width;

  for (let base = firstCoord; base <= endBase; base += coordinateInterval) {
    // Convert world coordinate to screen position
    // Max canvas size is 10000 x 10000, so we compute the delta in a way that avoids overflow
    const screenX = alignToLeftPixelGrid((baseShift + base) / basePerPixel - scrollX);

    // + 6 pixels to space the last tick text
    if (base > 0 && base < contigLength - (lastTickTextWidth + 6) * basePerPixel) {
      // Draw tick
      ctx.beginPath();
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, COORDINATE_TICK_HEIGHT);
      ctx.stroke();

      // Draw label
      if (scrollX + screenX + lastTickTextWidth * 2 < maxWidth) {
        ctx.fillText(
          base.toLocaleString("en-US"),
          screenX,
          alignToLeftPixelGrid(alignToLeftPixelGrid(2 + GROUP_HEADER_HEIGHT / 2)),
        );
      }
    }
  }

  // Draw the last tick
  let lastScreenX = alignToLeftPixelGrid((baseShift + contigLength) / basePerPixel - scrollX);
  const firstScreenX = alignToLeftPixelGrid(baseShift / basePerPixel - scrollX);

  // Draw the first tick
  ctx.beginPath();
  ctx.moveTo(firstScreenX, 0);
  ctx.lineTo(firstScreenX, COORDINATE_TICK_HEIGHT);
  ctx.stroke();

  if (firstScreenX + firstTickTextWidth + 6 < lastScreenX - lastTickTextWidth / 2) {
    ctx.fillText("0", firstScreenX, alignToLeftPixelGrid(alignToLeftPixelGrid(2 + GROUP_HEADER_HEIGHT / 2)));
  }

  // Draw the last tick
  ctx.beginPath();
  ctx.moveTo(lastScreenX, 0);
  ctx.lineTo(lastScreenX, COORDINATE_TICK_HEIGHT);
  ctx.stroke();

  // Draw label
  if (scrollX + lastScreenX + lastTickTextWidth > maxWidth) {
    lastScreenX = maxWidth - lastTickTextWidth / 2 - scrollX - 2; // -2 to account for the label
  }

  ctx.fillText(contigLength.toLocaleString("en-US"), lastScreenX, alignToLeftPixelGrid(2 + GROUP_HEADER_HEIGHT / 2));
  ctx.restore();
}

/**
 * This is the coordinates dashed lines in the background of each tracks.
 * Link to the coordinates of each group.
 * We need to draw them separated of the group header because there are not at the same layer level. Coordinates comes bellow the track, header on top.
 */
export function drawVerticalGridLines(
  height: number,
  baseShift: number,
  contigLength: number,
  baseData: {
    basePerPixel: number;
    coordinateInterval: number;
    ctx: CanvasRenderingContext2D;
    endBase: number;
    firstCoord: number;
    scrollX: number;
  },
) {
  const { basePerPixel, coordinateInterval, ctx, endBase, firstCoord, scrollX } = baseData;

  ctx.lineWidth = 1;
  const interval = basePerPixel <= 0.1 ? 1 : coordinateInterval;

  ctx.save();

  for (let base = firstCoord; base <= endBase; base += interval) {
    // Convert world coordinate to screen position
    const screenX = alignToLeftPixelGrid((baseShift + base) / basePerPixel - scrollX);

    // Only draw if coordinate is visible
    if (base >= 0 && base < contigLength) {
      ctx.beginPath();
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, height);

      if (base % coordinateInterval === 0) {
        ctx.setLineDash([5, 5]);
        ctx.strokeStyle = "rgba(36, 36, 36, 0.15)"; // major ticks
      } else {
        ctx.setLineDash([]);
        ctx.strokeStyle = "rgba(36, 36, 36, 0.05)"; // minor ticks
      }
      ctx.stroke();
    }
  }

  // Last bar
  const lastScreenX = alignToLeftPixelGrid((baseShift + contigLength) / basePerPixel - scrollX);

  ctx.beginPath();
  ctx.moveTo(lastScreenX, 0);
  ctx.lineTo(lastScreenX, height);

  ctx.setLineDash([5, 5]);
  ctx.strokeStyle = "rgba(36, 36, 36, 0.15)"; // major ticks
  ctx.stroke();

  ctx.setLineDash([]); // Reset the dash pattern

  ctx.restore();
}

export function drawHorizontalLine(ctx: CanvasRenderingContext2D, y: number) {
  ctx.beginPath();
  ctx.moveTo(0, alignToLeftPixelGrid(y));
  ctx.lineTo(ctx.canvas.width, alignToLeftPixelGrid(y));
  ctx.stroke();
}
